<template>
  <section>
    <div
      class="px-4 mx-auto max-w-3xl lg:px-0"
      :class="paddingTop === 'reduced' ? 'pt-6 pb-10 lg:pt-6 lg:pb-12' : 'py-12 lg:py-16'"
    >
      <div class="flex flex-col gap-4">
        <div
          v-if="badgeText"
          class="inline-flex items-start px-3.5 py-1.5 w-fit rounded border border-[#E0D5C5] bg-[#F0E8D8]"
        >
          <span
            class="text-xs tracking-wide text-[#6B3A22] uppercase font-ww-regular"
            v-html="badgeText"
          />
        </div>

        <h2
          v-if="title"
          class="text-22 font-bold leading-27 font-ww-bold text-[#3B2314]"
          v-html="title"
        />

        <nuxt-img
          v-if="imageSrc"
          :src="imageSrc"
          :alt="imageAlt"
          :width="imageWidth"
          :height="imageHeight"
          format="webp"
          loading="lazy"
          sizes="xs:100vw sm:740px lg:740px"
          class="object-cover w-full rounded-2xl aspect-[740/350]"
        />
        <div
          v-else
          class="w-full rounded-2xl aspect-[740/350] bg-[#2A2A2A]"
          aria-hidden="true"
        />

        <div
          class="text-17 leading-6 text-[#4A4A4A] font-ww-regular [&_p+p]:mt-5 [&_strong]:font-ww-bold [&_sup]:text-xs [&_h3]:mt-5 [&_h3]:text-22 [&_h3]:leading-27 [&_h3]:font-ww-bold [&_h3]:text-[#4A4A4A]"
          v-html="content"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
defineProps({
  badgeText: {
    type: String,
    default: 'Supplement Focus'
  },
  title: {
    type: String,
    default: 'Omega-3s are widely considered crucial for brain health.'
  },
  imageSrc: {
    type: String,
    default: '/assets/images/supplement-focus-omega3-mens.jpg'
  },
  imageAlt: {
    type: String,
    default: 'Omega-3 fish oil capsules on a yellow surface'
  },
  imageWidth: {
    type: Number,
    default: 740
  },
  imageHeight: {
    type: Number,
    default: 350
  },
  content: {
    type: String,
    default: `<p>These fatty acids improve blood flow to the brain, they're abundant in the cell membranes of brain cells and can preserve membrane health while helping your brain cells communicate with one another.</p>
<p>But, as DHA and EPA (long chain fatty acids) levels decrease with age, brain size can shrink and brain aging can accelerate.</p>
<p>Supporting the health of your brain with a quality omega-3 supplement (look for high DHA and EPA) have shown the potential to increase blood flow to the brain, reverse age-related inflammation, and support the neural structure of your brain.<sup>18</sup></p>
<p>This type of support can aid in overall cognitive health, decrease the risk of memory loss, protect against vascular dementia, etc.</p>
<h3>Use It Or Lose It</h3>
<p>Your brain plays a critical role in overall health.</p>
<p>So, to avoid natural decline, you need to use it regularly!</p>
<p>Read regularly.</p>
<p>Challenge multiple areas of your brain by doing puzzles such as sudoku, crosswords, and wordsearches.</p>
<p>Play chess with a friend.</p>
<p>Enjoy some time playing video games.</p>
<p>Each of these activities can be fun ways to help you unwind and destress, but they're also powerful ways to engage your mind, avoiding decline!</p>`
  },
  paddingTop: {
    type: String,
    default: 'reduced',
    validator: (value) => ['default', 'reduced'].includes(value)
  }
})

defineBuilderComponent({
  friendlyName: 'Content - Supplement Focus Article',
  inputs: [
    {
      name: 'badgeText',
      type: 'text',
      defaultValue: 'Supplement Focus'
    },
    {
      name: 'title',
      type: 'richText',
      defaultValue: 'Omega-3s are widely considered crucial for brain health.'
    },
    {
      name: 'imageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      defaultValue: '/assets/images/supplement-focus-omega3-mens.jpg'
    },
    {
      name: 'imageAlt',
      type: 'text',
      defaultValue: 'Omega-3 fish oil capsules on a yellow surface'
    },
    {
      name: 'imageWidth',
      type: 'number',
      defaultValue: 740
    },
    {
      name: 'imageHeight',
      type: 'number',
      defaultValue: 350
    },
    {
      name: 'content',
      type: 'richText',
      defaultValue: `<p>These fatty acids improve blood flow to the brain, they're abundant in the cell membranes of brain cells and can preserve membrane health while helping your brain cells communicate with one another.</p>
<p>But, as DHA and EPA (long chain fatty acids) levels decrease with age, brain size can shrink and brain aging can accelerate.</p>
<p>Supporting the health of your brain with a quality omega-3 supplement (look for high DHA and EPA) have shown the potential to increase blood flow to the brain, reverse age-related inflammation, and support the neural structure of your brain.<sup>18</sup></p>
<p>This type of support can aid in overall cognitive health, decrease the risk of memory loss, protect against vascular dementia, etc.</p>
<h3>Use It Or Lose It</h3>
<p>Your brain plays a critical role in overall health.</p>
<p>So, to avoid natural decline, you need to use it regularly!</p>
<p>Read regularly.</p>
<p>Challenge multiple areas of your brain by doing puzzles such as sudoku, crosswords, and wordsearches.</p>
<p>Play chess with a friend.</p>
<p>Enjoy some time playing video games.</p>
<p>Each of these activities can be fun ways to help you unwind and destress, but they're also powerful ways to engage your mind, avoiding decline!</p>`
    },
    {
      name: 'paddingTop',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'reduced'
    }
  ]
})
</script>
